/**
 * The wipe between the index and a museum.
 *
 * Entering an exhibition swaps the entire page: the index tears down, a WebGL context is
 * created, and several megabytes of geometry begin loading. Without cover, that reads as
 * a stall followed by a flash. The curtain closes over the old view, the swap happens
 * behind it, and it opens onto the new one — the same reason a theatre has one.
 *
 * The leading edge carries the Al-Qatt band, so the one moment the interface asserts
 * itself is also the moment it shows whose visual tradition it belongs to.
 *
 * Under prefers-reduced-motion nothing animates: the curtain is never inserted and the
 * caller's swap happens immediately.
 */

import qattBand from "./ornament/qatt-divider.webp?url";
import { motionAllowed } from "./motion";
import { splitWords } from "./opening";

const SWEEP_MS = 620;

/**
 * Run `swap` behind a closed curtain, then open it. Resolves once the swap has happened,
 * not once the curtain has finished opening, so callers are never blocked on decoration.
 */
export async function wipe(swap: () => void | Promise<void>): Promise<void> {
  if (!motionAllowed()) {
    await swap();
    return;
  }

  const curtain = document.createElement("div");
  curtain.className = "curtain";
  curtain.setAttribute("aria-hidden", "true");

  const band = document.createElement("div");
  band.className = "curtain__band";
  band.style.backgroundImage = `url(${qattBand})`;
  curtain.append(band);
  document.body.append(curtain);

  await settle(curtain, "curtain--closed");
  try {
    await swap();
  } finally {
    // Opening is presentation only. If the swap threw, the error UI it rendered still
    // has to become visible, so the curtain must come off either way.
    curtain.classList.add("curtain--open");
    window.setTimeout(() => curtain.remove(), SWEEP_MS + 60);
  }
}

const PASSAGE_IN_MS = 620;
const PASSAGE_HOLD_MS = 420;
const PASSAGE_OUT_MS = 700;

/**
 * The passage between one heritage and another.
 *
 * Changing region is not the same move as entering an exhibition, and it should not
 * feel like one. The curtain is a swap; this is arrival. The page darkens, a band of
 * light opens across it, and the name of the place you have reached rises out of it
 * before the light lifts away.
 *
 * It earns its length by doing a job. Switching from Saudi Arabia to Greece replaces
 * every photograph on the page at once, and without a beat in between that reads as a
 * glitch — the same content flickering into different content. Naming the destination
 * mid-passage tells the visitor what just happened while the work of doing it happens
 * behind the light.
 */
export async function travel(name: string, swap: () => void | Promise<void>): Promise<void> {
  if (!motionAllowed()) {
    await swap();
    return;
  }

  const passage = document.createElement("div");
  passage.className = "passage";
  // Decoration around a swap the page announces elsewhere; a screen reader that read
  // the destination here would hear it twice, once from the light and once from the
  // strip's own current-region state.
  passage.setAttribute("aria-hidden", "true");

  const glow = document.createElement("div");
  glow.className = "passage__glow";
  const title = document.createElement("p");
  title.className = "passage__name";
  title.append(splitWords(name));
  passage.append(glow, title);
  document.body.append(passage);

  await settle(passage, "passage--lit", PASSAGE_IN_MS);
  try {
    await swap();
    // The name is legible for a beat after the swap, so the arrival is read as arrival
    // rather than as a flash the eye has to reconstruct afterwards.
    await pause(PASSAGE_HOLD_MS);
  } finally {
    passage.classList.add("passage--gone");
    window.setTimeout(() => passage.remove(), PASSAGE_OUT_MS + 60);
  }
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Apply a class on the next frame and resolve when its transition ends. */
function settle(element: HTMLElement, className: string, ms = SWEEP_MS): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    element.addEventListener("transitionend", finish, { once: true });
    // A dropped transitionend would strand the page behind an opaque curtain, so the
    // timeout is a guarantee rather than an optimisation.
    window.setTimeout(finish, ms + 120);
    requestAnimationFrame(() => {
      // Two frames: one for the element to be laid out, one for the class to transition
      // rather than apply instantly.
      requestAnimationFrame(() => element.classList.add(className));
    });
  });
}
